import React, {ReactNode, useEffect, useState} from 'react';
import {Modal, Text, TextField} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';

/**
 * Generic confirmation dialog using the design-system Modal.
 *
 * - `description` may be a string or any React node (e.g. multiple Text blocks, Link, FormattedMessage) for rich content.
 * - Optional `confirmPhrase` requires the user to type that exact phrase (after trim) before the primary action is enabled.
 * - Optional `primaryButtonDisabled` disables the primary button when not using type-to-confirm mode.
 */
export enum ConfirmationModalPrimaryButton {
	CONFIRM = 'CONFIRM',
	SAVE = 'SAVE',
	DELETE = 'DELETE',
}

type ConfirmationModalProps = {
	title: React.ReactNode;
	description: string | ReactNode;
	callbackPositive: () => void;
	closeModal: () => void;
	autoClosePositive?: boolean;
	autoCloseNegative?: boolean;
	callbackNegative?: () => void;
	includeDefaultMessage?: boolean;
	confirmationModalPrimaryButton?: ConfirmationModalPrimaryButton;
	/** When set, user must type this exact string (trimmed) to enable the primary button. `primaryButtonDisabled` is ignored. */
	confirmPhrase?: string;
	/** When `confirmPhrase` is not used, disables the primary footer button. */
	primaryButtonDisabled?: boolean;
	/** Design-system modal width preset. Defaults to SMALL. */
	modalSize?: (typeof Modal.SIZE)[keyof typeof Modal.SIZE];
};

const ConfirmationModal = ({
	title,
	description,
	autoClosePositive = true,
	autoCloseNegative = true,
	callbackPositive,
	callbackNegative,
	closeModal,
	includeDefaultMessage = true,
	confirmationModalPrimaryButton = ConfirmationModalPrimaryButton.CONFIRM,
	confirmPhrase,
	primaryButtonDisabled = false,
	modalSize = Modal.SIZE.SMALL,
}: ConfirmationModalProps): JSX.Element => {
	const [typeToConfirmInput, setTypeToConfirmInput] = useState('');
	const isTypeToConfirmMode = Boolean(confirmPhrase);

	useEffect(() => {
		setTypeToConfirmInput('');
	}, [confirmPhrase]);

	const primaryDisabled = isTypeToConfirmMode ? typeToConfirmInput.trim() !== confirmPhrase : primaryButtonDisabled;

	const confirm = () => {
		callbackPositive();
		if (autoClosePositive) {
			closeModal();
		}
	};
	const cancel = () => {
		if (callbackNegative) {
			callbackNegative();
		}
		if (autoCloseNegative) {
			closeModal();
		}
	};

	return (
		<Modal size={modalSize} onCloseModal={closeModal}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				{typeof description === 'string' ? <Text>{description}</Text> : description}

				{includeDefaultMessage && (
					<Text>
						<FormattedMessage id="common.are_you_sure_proceed" />
					</Text>
				)}
				{isTypeToConfirmMode && (
					<div style={{maxWidth: 180}}>
						<TextField
							data-cy="confirmation-modal-type-to-confirm-input"
							value={typeToConfirmInput}
							onValueChange={setTypeToConfirmInput}
							width="flex"
						/>
					</div>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={confirm}
					dataCy="confirmation-modal-primary-button"
					disabled={primaryDisabled}
				>
					{confirmationModalPrimaryButton === ConfirmationModalPrimaryButton.SAVE ? (
						<FormattedMessage id="common.save" />
					) : (
						<FormattedMessage id="common.confirm" />
					)}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={cancel} dataCy="confirmation-modal-secondary-button">
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ConfirmationModal;
